// @instruction-md/render — the embeddable renderer (RFC-0025, ADR-0028).
//   import { define, render } from "@instruction-md/render";   // browser
//   import { renderToString } from "@instruction-md/render/ssr"; // node / static sites
import { InstructionMd } from "./elements/instruction-md";
import type { RenderMeta, RenderOptions, View } from "./project";

export { InstructionMd, DEFAULT_REGISTRY } from "./elements/instruction-md";
export { project, sourceLines, type Node, type Block, type RenderModel, type RenderOptions, type RenderMeta, type View, type FormField } from "./project";
export { escapeHtml, slug } from "./md";
export { readInstruction, metaFromRead } from "./registry-client";
export { outline, resolveAnchor, type Outline, type OutlineEntry, type OutlineSection } from "./outline";
export { enhanceDiagrams, setMermaidLoader } from "./diagrams";
export { enhanceLayouts } from "./layouts";
export { sanitizeSvg, workflowSvg } from "./figures";

export const ELEMENT_NAME = "instruction-md";

/** Register the custom elements (idempotent). Returns false when there is no DOM. */
export function define(): boolean {
  if (typeof customElements === "undefined") return false;
  if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, InstructionMd);
  return true;
}

export interface RenderCallOptions {
  view?: View;
  views?: View[];
  theme?: "light" | "dark" | "auto";
  params?: Record<string, unknown>;
  /** The document's parameters as inputs above it, to try values without editing the text. */
  inputs?: boolean;
  /** With inputs: a Save button whose values the host writes into the text (`imd-params-save`). */
  saveInputs?: boolean;
  allowImages?: boolean;
  /** Show the back matter (rules at a glance, guardrails, figures); default: with the full chrome. */
  backMatter?: boolean;
  /** The contents: a rail beside the text when there is room (\`side\`), above it (\`agenda\`) or none. */
  toc?: "side" | "agenda" | "none";
  meta?: RenderMeta;
  /** The host's approved integrations: an integration is marked as one, and under a restriction an object outside
   *  them is marked as not approved (§17). */
  integrations?: RenderOptions["integrations"];
}

/** Render `text` into `container` (an existing <instruction-md> is reused). */
export function render(container: Element, text: string, options: RenderCallOptions = {}): InstructionMd {
  define();
  let el = container.tagName.toLowerCase() === ELEMENT_NAME ? (container as InstructionMd) : (container.querySelector(ELEMENT_NAME) as InstructionMd | null);
  if (!el) { el = document.createElement(ELEMENT_NAME) as InstructionMd; container.appendChild(el); }
  if (options.view) el.view = options.view;
  if (options.views) el.views = options.views.join(",");
  if (options.theme) el.theme = options.theme;
  if (options.params) el.params = JSON.stringify(options.params);
  if (options.inputs !== undefined) el.inputs = options.inputs;
  if (options.saveInputs !== undefined) el.saveInputs = options.saveInputs;
  if (options.allowImages !== undefined) el.allowImages = options.allowImages;
  if (options.backMatter !== undefined) el.backMatter = options.backMatter ? "on" : "off";
  if (options.toc) el.toc = options.toc;
  if (options.meta) el.meta = options.meta;
  if (options.integrations !== undefined) el.integrations = options.integrations;
  el.text = text;
  return el;
}

/** Upgrade `[data-instruction-md]` elements: their text content (or data-src / data-ref) becomes an <instruction-md>. */
export function autoload(root: ParentNode = document): number {
  if (!define()) return 0;
  let n = 0;
  root.querySelectorAll<HTMLElement>("[data-instruction-md]").forEach((host) => {
    if (host.dataset.instructionMdUpgraded) return;
    host.dataset.instructionMdUpgraded = "1";
    const el = document.createElement(ELEMENT_NAME) as InstructionMd;
    for (const [k, v] of Object.entries(host.dataset)) {
      if (k === "instructionMd" || k === "instructionMdUpgraded" || v === undefined) continue;
      el.setAttribute(k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`), v);
    }
    if (!host.dataset.src && !host.dataset.ref) el.text = host.textContent ?? "";
    host.replaceChildren(el);
    n++;
  });
  return n;
}
